import { useState } from "react";
import { DateTime } from "luxon";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDown01Icon,
  Tick02Icon
} from "@hugeicons-pro/core-stroke-rounded";
import {
  Badge,
  BadgeSize,
  BadgeVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  Show
} from "shared/components";
import type { UnderwritingFinding } from "api/underwriting-agents/types";
import { useAuthContext } from "providers/Auth";
import { cx } from "shared/utils";

const StatusBadge = ({
  label,
  variant,
  dotClassName
}: {
  label: string;
  variant: BadgeVariant;
  dotClassName: string;
}) => (
  <Badge
    variant={variant}
    size={BadgeSize.SM}
    className="items-center"
    leftElement={
      <span
        className={cx(
          "inline-block size-2 shrink-0 rounded-full",
          dotClassName
        )}
      />
    }
  >
    {label}
  </Badge>
);

export const NeedsReviewBadge = ({ label }: { label: string }) => (
  <StatusBadge
    label={label}
    variant={BadgeVariant.ERROR}
    dotClassName="bg-danger-500"
  />
);

export const ReviewedBadge = ({ label }: { label: string }) => (
  <StatusBadge
    label={label}
    variant={BadgeVariant.SUCCESS}
    dotClassName="bg-success-600"
  />
);

const RuleDisclosure = ({ text }: { text: string }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-lg bg-background-900">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-sm font-medium text-content-400"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        Rule
        <HugeiconsIcon
          icon={ArrowDown01Icon}
          size={16}
          className={cx("shrink-0 transition-transform", open && "rotate-180")}
        />
      </button>
      <Show if={open}>
        <p className="-mt-0.5 px-3 pb-2.5 text-sm text-content-400">{text}</p>
      </Show>
    </div>
  );
};

const ReviewedBy = ({ finding }: { finding: UnderwritingFinding }) => {
  const { auth } = useAuthContext();
  const reviewer =
    finding.reviewed_by?.id === auth?.id ? "You" : finding.reviewed_by?.name;
  const reviewedAt = finding.reviewed_at
    ? DateTime.fromISO(finding.reviewed_at).toFormat("MMM d 'at' h:mm a")
    : "";

  return (
    <p className="text-sm text-content-400">
      {reviewer
        ? `Reviewed by ${reviewer}, ${reviewedAt}`
        : `Reviewed ${reviewedAt}`}
    </p>
  );
};

/** One underwriting agent finding: its review state, what was found, and the rule it broke. */
export const FindingCard = ({
  finding,
  onReview,
  onUndo,
  pending
}: {
  finding: UnderwritingFinding;
  onReview: (findingId: number) => void;
  onUndo: (findingId: number) => void;
  pending: boolean;
}) => (
  <div className="space-y-3">
    <div className="flex items-center gap-3">
      <div className="flex-1">
        {finding.reviewed_at ? (
          <ReviewedBadge label="Reviewed" />
        ) : (
          <NeedsReviewBadge label="Needs review" />
        )}
      </div>
      {finding.reviewed_at ? (
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.XS}
          loading={pending}
          onClick={() => onUndo(finding.id)}
        >
          Undo
        </Button>
      ) : (
        <Button
          variant={ButtonVariant.BASE}
          size={ButtonSize.XS}
          HugeIcon={Tick02Icon}
          loading={pending}
          onClick={() => onReview(finding.id)}
        >
          Mark as reviewed
        </Button>
      )}
    </div>
    <p className="text-base text-content-1000">{finding.finding}</p>
    <RuleDisclosure text={finding.rule_text} />
    <Show if={Boolean(finding.reviewed_at)}>
      <ReviewedBy finding={finding} />
    </Show>
  </div>
);
